import React, { useState, useContext, Suspense } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { Spin, Result, Button, theme } from 'antd';
import HeaderContainer from './HeaderContainer';
import DashboardView from './components/DashboardView/DashboardView';
import SettingsView from './components/DashboardView/SettingsView.tsx';
import { logger } from './services/logger.client';
import { useAuth } from './hooks/useAuth';
import { useRenderLoopDetection } from './hooks/hookUtils';
import { GridProvider } from './components/GridProvider/GridProvider';
import ProtectedRoute from './ProtectedRoute';
import { BootstrapProvider, useBootstrapCoordinator } from './hooks/bootstrap';
import { ConfirmationProvider } from './providers/ConfirmationProvider';
import { useImageLibraryStore } from './state/createImageLibraryStore.ts';
import EditModalContainer from './modals/EditModal/EditModalContainer.tsx';
import CharacterSheetModalContainer from './modals/CharacterSheetModal/CharacterSheetModalContainer.tsx';
import DiceRollerModal from './modals/DiceRollerModal/DiceRollerModal.tsx';
import SoundscapeModal from './modals/SoundscapeModal/SoundscapeModal.tsx';
import WatchOrderModalContainer from './modals/WatchOrderModal/WatchOrderModalContainer.tsx';
import SessionModalContainer from './modals/SessionModal/SessionModalContainer.tsx';
import ScratchpadModalContainer from './modals/ScratchpadModal/ScratchpadModalContainer.tsx';
import CampaignLogModalContainer from './modals/CampaignLogModal/CampaignLogModalContainer.tsx';
import EncounterModal from './modals/EncounterModal/EncounterModal.tsx';
import AddManualCombatantModalContainer from './modals/AddManualCombatantModal/AddManualCombatantModalContainer.tsx';
import AddImageModalContainer from './modals/AddImageModal/AddImageModalContainer.tsx';
import AddEventModalContainer from './modals/AddEventModal/AddEventModalContainer.tsx';
import ImageSelectModal from './modals/ImageSelectModal/ImageSelectModal.tsx';
import AddTerrainTypeModalContainer from './modals/AddTerrainTypeModal/AddTerrainTypeModalContainer.tsx';
import CreateRegionModal from './modals/CreateRegionModal/CreateRegionModal.tsx';
import GridSettingsModalContainer from './modals/GridSettingsModal/GridSettingsModalContainer.tsx';
import AddRelationshipModalContainer from './modals/AddRelationshipModal/AddRelationshipModalContainer.tsx';
import type { RenderedImageRect } from './types/types.ts';
import PublicLayout from './PublicLayout';
import { ApiError } from './services/apiErrors';
import GlobalErrorBoundary from './GlobalErrorBoundary';
import { FeatureErrorBoundary } from './components/FeatureErrorBoundary';
import { useConfirmation } from './providers/ConfirmationContext.ts';
import { useUiStore } from './state/createUiStore.ts';
import { useCombatSync } from './hooks/useCombatSync';

// Lazy load all the page/view components
const LoginPage = React.lazy(() => import('./auth/LoginPage'));
const RegistrationPage = React.lazy(() => import('./auth/RegistrationPage'));
const ForgotPasswordPage = React.lazy(() => import('./auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./auth/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./auth/VerifyEmailPage'));
const ResendVerificationPage = React.lazy(() => import('./auth/ResendVerificationPage'));
const DataView = React.lazy(() => import('./features/data/DataView/DataViewContainer'));
const WorldBuildingView = React.lazy(() => import('./features/world-building/WorldBuildingViewContainer'));
const AdminPage = React.lazy(() => import('./features/admin/AdminPage'));


/**
 * Application initialization component.
 *
 * Uses the modular bootstrap system (BootstrapProvider + useBootstrapCoordinator)
 * which provides two-phase initialization:
 * - Critical phase: Blocks UI render until essential data is loaded
 * - Cache phase: Loads reference data in background without blocking
 *
 * @see react-ui/src/hooks/bootstrap/useBootstrapCoordinator.ts for implementation details
 */
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useRenderLoopDetection('AppInitializer');
  const { isAuthStateHydrated } = useAuth();
  const { isLoading, isCacheLoading, isError, error } = useBootstrapCoordinator();

  // Debug: Log decision-making state on every render
  logger.debug(
    {
      component: 'AppInitializer',
      isAuthStateHydrated,
      isLoading,
      isCacheLoading,
      isError,
      hasError: error !== null,
      errorName: error?.name ?? null,
      errorMessage: error?.message ?? null
    },
    'AppInitializer: Render decision state'
  );

  // Wait for the auth state to be hydrated from localStorage before doing anything.
  // This prevents a flicker where the app might briefly think the user is logged out.
  if (!isAuthStateHydrated) {
    logger.info(
      { decision: 'showPreparing', reason: 'isAuthStateHydrated is false' },
      'AppInitializer: Showing "Preparing application..." spinner'
    );
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Preparing application..." />
      </div>
    );
  }

  if (isLoading) {
    logger.info(
      { decision: 'showLoading', isLoading, isCacheLoading, isError, hasError: error !== null },
      'AppInitializer: Showing "Loading application..." spinner'
    );
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading application..." />
      </div>
    );
  }

  if (isError || error) { // Also check if the error object exists
    logger.error(
      {
        decision: 'showError',
        isError,
        hasError: error !== null,
        errorName: error?.name ?? null,
        errorMessage: error?.message ?? null,
        isApiError: error instanceof ApiError,
        status: error instanceof ApiError ? error.status : null
      },
      'AppInitializer: Showing error screen'
    );

    // Let's make the error message more helpful
    const isApiError = error instanceof ApiError;
    const title = isApiError ? `Error ${error.status}` : "Failed to Load Application";
    let subTitle = error?.message ?? 'An unexpected error occurred.';

    if (isApiError && error.status === 502) {
        subTitle = "The server is temporarily unavailable (Bad Gateway). Please try again in a few moments.";
    } else if (isApiError && error.status >= 500) {
        subTitle = "A server error occurred while loading application data. Our team has been notified.";
    }

    return (
      <Result
        status={isApiError && error.status < 500 ? "warning" : "500"}
        title={title}
        subTitle={subTitle}
        extra={<Button type="primary" onClick={() => window.location.reload()}>Retry</Button>}
      />
    );
  }

  // Log if cache is still loading in background (non-blocking)
  if (isCacheLoading) {
    logger.debug(
      { isCacheLoading },
      'AppInitializer: Cache still loading in background, rendering children anyway'
    );
  }

  logger.info(
    { decision: 'showChildren', isLoading, isCacheLoading, isError, hasError: error !== null },
    'AppInitializer: State loaded successfully, rendering application children'
  );
  return <>{children}</>;
};

const AppLayout = () => {
  logger.info('AppLayout: Rendering main application layout.');
  const { token } = theme.useToken();
  // Confirmation hook
  const { isAuthConfirming } = useConfirmation();

  return (
    <div
      className={isAuthConfirming ? 'blur-background' : ''}
      style={{ backgroundColor: token.colorBgLayout, minHeight: '100vh' }}
    >
      <HeaderContainer />
      <main data-testid="app-main">
        {/* Outlet for nested protected routes */}
        <Outlet />
      </main>
    </div>
  );
};

/**
 * A shell component that composes all protected layouts and providers.
 * This simplifies the route configuration by combining authentication checks,
 * context providers, and the main UI layout into a single element.
 */
const ProtectedAppShell = () => (

    <ProtectedRoute>
        <AppLayout />
    </ProtectedRoute>
);

const AppProvidersLayout: React.FC = () => {
  // Sync combat state to backend with debounce (ADR-033 Phase 3)
  useCombatSync();

  const uiState = useUiStore();

  // Image library is still needed by ImageSelectModal (not yet migrated to container)
  const imageLibrary = useImageLibraryStore((state) => state.images);

  return (
    <>
      <Outlet />
      <CharacterSheetModalContainer isOpen={uiState.isCharacterSheetModalOpen} onClose={uiState.hideCharacterSheetModal} player={uiState.characterSheetPlayer} />
      <EditModalContainer isOpen={uiState.isEditModalOpen} onClose={uiState.hideEditModal} context={uiState.editModalContext} />
      {(
        <>
          <DiceRollerModal isOpen={uiState.isDiceRollerOpen} onClose={uiState.hideDiceRollerModal} />
          <SoundscapeModal isOpen={uiState.isSoundscapeOpen} onClose={uiState.hideSoundscapeModal} />
          <WatchOrderModalContainer isOpen={uiState.isWatchOrderOpen} onClose={uiState.hideWatchOrderModal} />
          <SessionModalContainer isOpen={uiState.isSessionOpen} onClose={uiState.hideSessionModal} />
          <ScratchpadModalContainer isOpen={uiState.isScratchpadOpen} onClose={uiState.hideScratchpadModal} />
          <CampaignLogModalContainer isOpen={uiState.isCampaignLogOpen} onClose={uiState.hideCampaignLogModal} />
          <EncounterModal isOpen={uiState.isEncounterOpen} onClose={uiState.hideEncounterModal} result={uiState.encounterResult} />
          <AddManualCombatantModalContainer isOpen={uiState.isAddManualCombatantOpen} onClose={uiState.hideAddManualCombatantModal} />
          <AddImageModalContainer isOpen={uiState.isAddImageOpen} onClose={uiState.hideAddImageModal} />
          <AddEventModalContainer isOpen={uiState.isAddEventOpen} onClose={uiState.hideAddEventModal} />
          <ImageSelectModal isOpen={uiState.isImageSelectOpen} onClose={uiState.hideImageSelectModal} onSelect={(id) => { uiState.imageSelectCb(id); uiState.hideImageSelectModal(); }} images={imageLibrary} />
          <AddTerrainTypeModalContainer isOpen={uiState.isAddTerrainOpen} onClose={uiState.hideAddTerrainTypeModal} />
          <CreateRegionModal isOpen={uiState.isCreateRegionOpen} onClose={uiState.hideCreateRegionModal} onSave={(data) => { uiState.createRegionCb(data); uiState.hideCreateRegionModal(); }} />
          <GridSettingsModalContainer isOpen={uiState.isGridSettingsOpen} onClose={uiState.hideGridSettingsModal} />
          <AddRelationshipModalContainer isOpen={uiState.isAddRelationshipOpen} onClose={uiState.hideAddRelationshipModal} />
        </>
      )}
    </>
  );
};

// Create a context to provide the onImageRectChange function.
const ImageRectContext = React.createContext<(rect: RenderedImageRect | null) => void>(() => {});

const useImageRect = () => {
  return useContext(ImageRectContext);
};

/**
 * Shared routes component that defines all application routes.
 * Rendered within the bootstrap provider hierarchy.
 */
const AppRoutes: React.FC<{ onImageRectChange: (rect: RenderedImageRect | null) => void }> = ({ onImageRectChange }) => (
  <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>}>
    <Routes>
      {/* Public routes wrapped in a layout that provides the background color */}
      <Route element={<PublicLayout />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegistrationPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="resend-verification" element={<ResendVerificationPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<AppProvidersLayout />}>
        <Route element={<ProtectedAppShell />}>
          {/* Dashboard uses AppInitializer error handling, no FeatureErrorBoundary needed */}
          <Route index element={<DashboardView onImageRectChange={onImageRectChange} />} />
          <Route path="data" element={
            <FeatureErrorBoundary feature="Data Management">
              <DataView />
            </FeatureErrorBoundary>
          } />
          <Route path="world-building" element={
            <FeatureErrorBoundary feature="World Building">
              <WorldBuildingView />
            </FeatureErrorBoundary>
          } />
          <Route path="admin" element={
            <FeatureErrorBoundary feature="Administration">
              <ProtectedRoute allowedRoles={['ADMIN']}><AdminPage /></ProtectedRoute>
            </FeatureErrorBoundary>
          } />
          <Route path="settings" element={
            <FeatureErrorBoundary feature="Settings">
              <SettingsView />
            </FeatureErrorBoundary>
          } />
        </Route>
      </Route>
    </Routes>
  </Suspense>
);

/**
 * Main application component.
 *
 * Wraps the application with BootstrapProvider for two-phase initialization:
 * - Critical phase: Blocks UI render until essential session data is loaded
 * - Cache phase: Non-blocking background load of reference data (items, spells, etc.)
 *
 * @see react-ui/src/hooks/bootstrap/useBootstrapCoordinator.ts for bootstrap logic
 */
const Main: React.FC = () => {
  const onImageRectChange = useImageRect();

  logger.info('Main: Rendering with routing');

  return (
    <BootstrapProvider>
      <AppInitializer>
        <AppRoutes onImageRectChange={onImageRectChange} />
      </AppInitializer>
    </BootstrapProvider>
  );
};

export const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  logger.info('AppProviders: Setting up root providers.');
  const [imageRect, setImageRect] = useState<RenderedImageRect | null>(null);

  return (
    <GlobalErrorBoundary>
      <ConfirmationProvider>
        <ImageRectContext.Provider value={setImageRect}>
          <GridProvider imageRect={imageRect}>
            {children}
          </GridProvider>
        </ImageRectContext.Provider>
      </ConfirmationProvider>
    </GlobalErrorBoundary>
  );
};

const App: React.FC = () => {
  logger.info('App: Rendering root component.');
  return <Main />;
};

export default App;